import { ServiceSettings, Site, SiteListObject } from "~/entities/siteEntities";
import { isMenuOpen } from "./menuHelpers";
import { Availability, DaysOfWeek, TimeOfDay } from "~/entities/baseEntities";
import { timeFromObject } from "./dateHelper";
import { toZonedTime } from "date-fns-tz";
import { Menu } from "~/entities/menuEntities";
import { SiteWithPosition } from "~/modules/sites/components/SitesList/SitesList";

const isSameOrBefore = (end: TimeOfDay, start: TimeOfDay): boolean => {
  if (end.hour == start.hour) {
    return end.minute <= start.minute;
  } else {
    return end.hour <= start.hour;
  }
};
const isBefore = (end: TimeOfDay, start: TimeOfDay): boolean => {
  if (end.hour == start.hour) {
    return end.minute < start.minute;
  } else {
    return end.hour < start.hour;
  }
};

export const daysOfWeek: (keyof DaysOfWeek)[] = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

// Get the currently applicable availability, not the scheduled one
export const getApplicableStoreHours = (site: SiteListObject) =>
  site.openingTimes[0];

export const siteHasStoreHours = (site: SiteListObject) =>
  getApplicableStoreHours(site)?.availability?.length > 0;

export const isStoreOpen = (site: SiteListObject): boolean => {
  const localTime = toZonedTime(new Date(), site.timeZone);

  const todaysAvailability = getAvailabilityForDay(
    site,
    daysOfWeek[localTime.getDay()]
  );

  const timeOfDay: TimeOfDay = {
    hour: localTime.getHours(),
    minute: localTime.getMinutes(),
  };

  return todaysAvailability.some(
    (x) =>
      isSameOrBefore(x.startsAt, timeOfDay) && isBefore(timeOfDay, x.endsAt)
  );
};

// english readable store time
export const getReadableAvailability = (values: Availability[]): string => {
  if (values.length > 0) {
    return values
      .map((value) => {
        const isAlwaysOpen =
          value.startsAt.hour === 0 && value.endsAt.hour === 24;
        return isAlwaysOpen
          ? "Always open"
          : `${timeFromObject(value.startsAt)} - ${timeFromObject(
              value.endsAt
            )}`;
      })
      .join(", ");
  }
  return "Closed";
};

// english readable store time
export const getStoreTime = (site: SiteListObject) => {
  const currentDay = toZonedTime(new Date(), site.timeZone).getDay();
  const availability = getAvailabilityForDay(site, daysOfWeek[currentDay]);

  return getReadableAvailability(availability);
};

export const getMappedStoreAvailability = (
  availability: Availability[]
): Record<keyof DaysOfWeek, Availability[]> => {
  const availabilityMap: Record<keyof DaysOfWeek, Availability[]> = {
    monday: [],
    tuesday: [],
    wednesday: [],
    thursday: [],
    friday: [],
    saturday: [],
    sunday: [],
  };

  const normalisedAvailability = normaliseAvailability(availability);

  daysOfWeek.forEach((day) => {
    const times = normalisedAvailability.filter((x) => x.applies[day]);
    times.sort((a, b) => (isSameOrBefore(a.startsAt, b.startsAt) ? -1 : 1));

    const dayRes: Availability[] = [];

    for (const time of times) {
      if (
        dayRes.length > 0 &&
        isSameOrBefore(time.startsAt, dayRes[dayRes.length - 1].endsAt)
      ) {
        if (isSameOrBefore(time.endsAt, dayRes[dayRes.length - 1].endsAt)) {
          // skip because new time is completely within existing
        } else {
          dayRes[dayRes.length - 1].endsAt = time.endsAt;
        }
      } else {
        dayRes.push({
          startsAt: time.startsAt,
          endsAt: time.endsAt,
          applies: time.applies,
        });
      }
    }

    availabilityMap[day] = dayRes;
  });

  return availabilityMap;
};

export const getAvailabilityForDay = (
  site: SiteListObject,
  day: keyof DaysOfWeek
) => {
  const applicableStoreHours = getApplicableStoreHours(site);

  if (!applicableStoreHours) {
    return [];
  }
  return getMappedStoreAvailability(applicableStoreHours.availability)[day];
};

export const normaliseAvailability = (
  availability: Availability[]
): Availability[] => {
  const res: Availability[] = [];

  availability.forEach((a) => {
    if (isBefore(a.startsAt, a.endsAt)) {
      res.push(a);
    } else {
      res.push({
        applies: a.applies,
        startsAt: a.startsAt,
        endsAt: {
          hour: 24,
          minute: 0,
        },
      });

      if (a.endsAt.hour !== 0 || a.endsAt.minute !== 0) {
        res.push({
          applies: {
            monday: a.applies.sunday,
            tuesday: a.applies.monday,
            wednesday: a.applies.tuesday,
            thursday: a.applies.wednesday,
            friday: a.applies.thursday,
            saturday: a.applies.friday,
            sunday: a.applies.saturday,
          },
          startsAt: {
            hour: 0,
            minute: 0,
          },
          endsAt: a.endsAt,
        });
      }
    }
  });

  return res;
};

export const isServiceTypeAvailable = (
  site: Site | SiteWithPosition | null | undefined,
  orderTypeCode: string
): boolean => {
  return (
    site?.serviceAvailability?.find((s) => s.orderTypeCode === orderTypeCode)
      ?.available ?? true
  );
};

export const getServiceSettings = (
  site: Site | null | undefined,
  orderTypeCode: string
): ServiceSettings | null | undefined => {
  return site?.serviceSettings?.find((s) => s.orderTypeCode === orderTypeCode);
};

export const getServiceStatus = (
  site: Site | null | undefined,
  menu: Menu | null | undefined,
  orderTypeCode: string,
  requestedReadyTime: string | null | undefined
): {
  isAvailable: boolean;
  menuOpen: boolean;
  serviceAvailable: boolean;
} => {
  const menuOpen = menu
    ? isMenuOpen(menu, requestedReadyTime, site?.timeZone)
    : false;
  const serviceAvailable = isServiceTypeAvailable(site, orderTypeCode);

  return {
    isAvailable: menuOpen && serviceAvailable,
    menuOpen,
    serviceAvailable,
  };
};

export const mapSiteToSiteListObj = (site: Site) => {
  return {
    id: site.id,
    address: site.siteAddress,
    name: site.siteName,
    image: site.siteImage,
    serviceAvailability: site.serviceAvailability,
    serviceSettings: site.serviceSettings,
    orderTypes: site.orderTypes,
    branding: site.branding,
    openingTimes: site.openingTimes,
    timeZone: site.timeZone,
  };
};
